import {AppConfig} from "../../core/config/config.types";
import {FidelityData} from "../fidelity/fidelity.types";

export interface Cart {
  items: ArticleCart[],
  dinerMode: DinerMode
  paymentMethod: PaymentMethod,
  fidelityData?: FidelityData,
  orderStartTimestamp?: string,
  checkoutAdditionalData?: CheckoutData,
  orderId: string,
  activeCoupons: string[]
}


export type DinerMode = "take_away" | "restaurant";
export type PaymentMethod = "cash" | "credit_card"

/**
 * Additional data for checkout
 */
export interface CheckoutData {
  /**
   * Optional table number to deliver the order to. Used when the option to ask the table number is enabled and the customer inserts a table number.
   */
  tableNumber?: string;
}


export interface CartSsCheckout {
  items: ArticleSs[],
  kioskCode: string,
  dinerMode: DinerMode,
  paymentMethod: PaymentMethod,
  currencyCode: string,
  isNetVat: boolean,
  checkoutMode: "calculate" | "finalize" | "suspend",
  languageCode: string,
  /**
   * Represents a logged-in customer card code.
   */
  cardCode?: string,
  /**
   * Table number where the customer sits, to allow a worker to deliver the order to him/her.
   * Valued only when the ask table number option is set to true and a number is inserted
   */
  placeholder?: string,
  /**
   * Unique order identifier generated by the kiosk
   */
  orderId: string,
  /**
   * List of coupons currently applied to the current order
   */
  coupons: string[]
}

export interface UpsellDataCart {
  id: string;
  code: string;
  promotionalPrice?: number;
  parentCartEntryId?: string;
}


export interface CouponPromoDataCart {
  couponCode: string;
  promoCode?: string;
  /**
   * Final promotional price if specified
   */
  promotionalPrice?: number;
}

export class ArticleCart {
  //Cart entry id. Generated on creation.
  entryId: string;
  articleId: string;
  readableName: string;
  name: string;
  code: string;
  description: string;
  imageUrl?: string;
  vatValue: number;
  vatCode: string;
  quantity: number;
  /**
   * Original price, without any discount nor promotion as INTEGER (100 equals 1 euro)
   * Upsell, coupons and others in the future may specify an 'override' price that will be used as the price. This basePrice property will keep pointing to the original 'reference' price to display discounts.
   */
  basePrice: number;
  bundleSurcharge?: number;
  articleSubType: "product" | "modifier" | "bundle" | "bundle_part" | "group_modifier" | "ingredients";
  subArticles: Map<string, ArticleCart>;
  upsell?: UpsellDataCart;
  /**
   * Indicates if the article is promotional and comes from a coupon usage.
   * Only the parent article should have this populated. Modifiers and articles inside a bundle refer to a parent article.
   */
  couponPromo?: CouponPromoDataCart;

  constructor(params: {
    entryId?: string,
    articleId: string,
    readableName: string,
    name: string,
    code: string,
    description: string,
    imageUrl?: string,
    vatValue: number,
    vatCode: string,
    quantity: number,
    basePrice: number,
    bundleSurcharge?: number;
    discountPrice?: number,
    articleSubType: "product" | "modifier" | "bundle" | "bundle_part" | "group_modifier" | "ingredients",
    subArticles?: Map<string, ArticleCart>
    upsell?: UpsellDataCart,
    couponPromo?: CouponPromoDataCart
  }) {

    this.entryId = !params.entryId ? crypto.randomUUID() : params.entryId;

    this.articleId = params.articleId;
    this.readableName = params.readableName;
    this.name = params.name;
    this.code = params.code;
    this.description = params.description;
    this.imageUrl = params.imageUrl;
    this.vatValue = params.vatValue;
    this.vatCode = params.vatCode;
    this.quantity = params.quantity;
    this.basePrice = params.basePrice;
    this.bundleSurcharge = params.bundleSurcharge;
    this.articleSubType = params.articleSubType;
    this.upsell = params.upsell;
    this.subArticles = params.subArticles || new Map<string, ArticleCart>();
    this.couponPromo = params.couponPromo;
  }

  /**
   * Returns true if the article is "promotional" for any condition that transforms the article in a promotional article.
   */
  get isPromotional(): boolean {
    return this.isUpsellPromotional || this.isCouponPromotional;
  }

  get isUpsellPromotional(): boolean {
    return this.upsell !== undefined;
  }

  get isCouponPromotional(): boolean {
    return this.couponPromo !== undefined;
  }

  /**
   * Returns the base price of the article to use for calculations as INTEGER.
   * Calculations and final prices should use this property and not the basePrice.
   * Base price is the final price to use. Reference price is the original price without any discount.
   * There's a hierarchy of prices to apply. From the highest priority: coupon > upsell > reference.
   */
  get price(): number {
    return this.couponPromo?.promotionalPrice ??
      this.upsell?.promotionalPrice ??
      this.basePrice;
  }

  /**
   * Returns the discounted price of the article as INTEGER or undefined if no promotions are applied.
   * As of now, promotional prices include coupon promos and upsells.
   */
  get promotionalPrice(): number | undefined {
    return this.couponPromo?.promotionalPrice ??
      this.upsell?.promotionalPrice
  }

  /**
   * The final row price, including any discount applied
   */
  finalPriceTotal(promotionsApplied?: SsCheckoutRespDiscountInfo[]) {

    // Start from the base total price and return if no promotions
    const baseTotal = ArticleCart.totalPrice(this);

    if (!promotionsApplied)
      return baseTotal;

    // Search and subtract every discount that applies to this row
    const discountAmount = promotionsApplied.reduce((acc, discount) => acc + discount.amount, 0) / 100.0;

    return Math.max(baseTotal - discountAmount, 0);
  };


  static create(): ArticleCart {
    return new ArticleCart({
      articleId: '',
      readableName: '',
      basePrice: 0,
      quantity: 0,
      code: '',
      name: '',
      description: '',
      articleSubType: 'product',
      vatCode: '',
      vatValue: 0.0
    });
  }

  /**
   * Calculates the price of a cart entry based on the base price.
   * Useful to calculate the final price of a row before any discount applied.
   * Special prices such as the coupon one or upsell are acccounted for here, as we base the calculation on the baseprice.
   * @param article
   */
  static totalPrice(article: ArticleCart): number {

    let price = article.price;

    price += ArticleCart.childrenPrice(article);

    return (price * article.quantity) / 100.0;
  }

  /**
   * Calculates the price of a cart entry based on the base price
   * Useful to calculate the subtotal of the cart (full price) without any discount or special price.
   * Special prices such as the coupon one or upsell are not acccounted for here.
   * @param article
   */
  static baseTotalPrice(article: ArticleCart): number {

    let price = article.basePrice;

    price += ArticleCart.childrenPrice(article);

    return (price * article.quantity) / 100.0;
  }


  /**
   * Calculates the sum of prices of the children.
   * Does not include the parent's price (the passed article).
   * Returns a price variabile as integer.
   * @param article
   * @private
   */
  private static childrenPrice(article: ArticleCart): number {

    let price = 0.0;

    if (article.articleSubType == "product") {
      for (let group of article.subArticles.values())
        for (let mod of group.subArticles.values()) {
          price += (mod.price * mod.quantity);
        }
    } else {
      //handling a bundle
      for (let section of article.subArticles.values())
        for (let article of section.subArticles.values()) {
          //Sum optional bundle surcharge to price.
          price += article?.bundleSurcharge ?? 0;
          for (let group of article.subArticles.values())
            for (let mod of group.subArticles.values()) {
              price += (mod.price * mod.quantity);
            }
        }

    }

    return price;
  }

  static getSelectedModifiers(article: ArticleCart): ArticleCart[] {
    return Array.from(article.subArticles.values())
      .flatMap(group => Array.from(group.subArticles.values()));
  }


}

export interface ArticleSs {
  /**
   * Cart entry id of the kiosk. Valid only for parent articles.
   */
  cartEntryId?: string;
  name: string;
  readableName: string;
  code: string;
  vatPercent: number;
  vatCode: string;
  quantity: number;
  isCombo: boolean;
  /**
   * Final item price. This will be a special price in case of discounted upsells or promotional items with a specific price.
   */
  price: number;
  /**
   * Original price (from spl or article), with no discounts or special prices accounted for.
   */
  basePrice: number;
  bundleSurcharge?: number;
  subProducts: ArticleSs[];
  isCouponPromotional?: boolean;
  appliedCouponCode?: string;
}

/**
 * Represents a response from SS for Checkout APIs
 */
export interface CartSsCheckoutResponse {

  /**
   * Indicates the subtotal (with no discounts counted) of the cart as INTEGER (eg: 100 for 1 euro)
   */
  subTotal: number;
  /**
   * Indicates the TOTAL discount amount (the sum of the discounts) to apply as INTEGER (eg: 100 for 1 euro).
   * Includes discounts related to whole cart and single items
   */
  totalDiscount?: number;
  /**
   * Indicates the final amount of the order as INTEGER. (eg: 100 for 1 euro)
   */
  cartTotal: number;
  /**
   * Array of discounts related to single cart entries.
   * Note: we could have multiple discounts related to the same cart entry. Each discount has its own amount.
   */
  cartEntriesDiscounts?: SsCheckoutRespDiscountInfo[];

  /**
   * Array of discounts related to the whole cart
   */
  cartDiscounts?: SsCheckoutRespDiscountInfo[];

  invalidatedCoupons?: SsCheckoutRespInvalidatedCouponInfo[];

  // Other returned fields not used as of now.
  transactionId?: string;
  transactionNum?: string;
  transactionDate?: string;
  isSuspended?: boolean;
  kdsCheckNumber?: string;
  numItems?: number;
  billTotal?: number;
}

export interface SsCheckoutRespDiscountInfo {
  /**
   * Used to link a discount to a specific cart entry. Used only by cartEntriesDiscounts elements of the response.
   */
  cartEntryId?: string;
  /**
   * Amount of the discount as INTEGER (100 for 1 euro)
   */
  amount: number;
  /**
   * Name of the promotion applied
   */
  promoName?: string;
  /**
   * Coupon code that has triggered the promotion
   */
  couponCode?: string;
}

export interface SsCheckoutRespInvalidatedCouponInfo {
  couponCode: string;
  /**
   * Error code used to explain to the customer why the coupon was removed.
   */
  reasonCode?: string;
}


export function convertCartToSsCheckout(cart: Cart, ssMode: "calculate" | "finalize" | "suspend", config: AppConfig, languageCode: string): CartSsCheckout {

  const dto: CartSsCheckout = {
    kioskCode: config.kioskCode,
    dinerMode: cart.dinerMode,
    items: [],
    paymentMethod: cart.paymentMethod,
    currencyCode: config.paymentsSettings.currencyCode,
    isNetVat: config.paymentsSettings.isNetVat,
    checkoutMode: ssMode,
    languageCode: languageCode,
    cardCode: cart.fidelityData?.cardCode,
    placeholder: cart?.checkoutAdditionalData?.tableNumber,
    orderId: cart.orderId,
    coupons: cart.activeCoupons
  };

  for (const item of cart.items) {
    const prodCheckout: ArticleSs = {
      cartEntryId: item.entryId,
      code: item.code,
      vatPercent: item.vatValue,
      isCombo: item.articleSubType == "bundle",
      name: item.name,
      readableName: item.readableName,
      price: item.price,
      basePrice: item.basePrice,
      quantity: item.quantity,
      vatCode: item.vatCode,
      subProducts: [],
      isCouponPromotional: item.isCouponPromotional,
      appliedCouponCode: item.couponPromo?.couponCode,
    }

    if (prodCheckout.isCombo) {
      for (const section of item.subArticles.values())
        for (const article of section.subArticles.values()) {
          const sectionProd: ArticleSs = {
            code: article.code,
            vatPercent: article.vatValue,
            vatCode: article.vatCode,
            name: article.name,
            isCombo: false,
            readableName: article.readableName,
            price: item.price,
            basePrice: item.basePrice,
            bundleSurcharge: article?.bundleSurcharge,
            quantity: article.quantity,
            subProducts: [],
            // Ignore promotional fields, articles inside of a bundle are not directly promotional, they refer to the parent article
          }

          for (const group of article.subArticles.values())
            for (const modifier of group.subArticles.values()) {
              const modCheckout: ArticleSs = {
                code: modifier.code,
                vatPercent: modifier.vatValue,
                vatCode: modifier.vatCode,
                name: modifier.name,
                isCombo: false,
                readableName: modifier.readableName,
                price: item.price,
                basePrice: item.basePrice,
                quantity: modifier.quantity,
                subProducts: []
                // Ignore promotional fields, modifiers are not directly promotional, they refer to the parent article
              }

              //Special case for ingredients: if group is ingredient, set quantity to negative.
              if (group.articleSubType == "ingredients")
                modCheckout.quantity *= -1;

              sectionProd.subProducts.push(modCheckout);
            }

          prodCheckout.subProducts.push(sectionProd);
        }
    } else {
      for (const group of item.subArticles.values())
        for (const modifier of group.subArticles.values()) {
          const modCheckout: ArticleSs = {
            cartEntryId: item.entryId,
            code: modifier.code,
            vatPercent: modifier.vatValue,
            vatCode: modifier.vatCode,
            name: modifier.name,
            isCombo: false,
            readableName: modifier.readableName,
            price: item.price,
            basePrice: item.basePrice,
            quantity: modifier.quantity,
            subProducts: [],
            // Ignore promotional fields, modifiers are not directly promotional, they refer to the parent article
          }

          //Special case for ingredients: if group is ingredient, set quantity to negative.
          if (group.articleSubType == "ingredients")
            modCheckout.quantity *= -1;

          prodCheckout.subProducts.push(modCheckout);
        }

    }

    dto.items.push(prodCheckout);
  }

  return dto;
}
